// frontend/app/components/lessonPlans/authoring/loopValidation.js
//
// The Loop-shaped checks the Review step runs on top of validateDraft.
//
// These are warnings, not errors: a lesson missing a Reflect block is a worse
// lesson, not a broken one, and refusing to publish it would make the wizard
// unusable for the four-step lesson a teacher actually wants on Friday. What
// they must never do is stay quiet — a lesson whose only tagged blocks are
// predictions cannot produce a mastery score at all, and that is invisible
// until a teacher opens Insights and finds it empty.

import {
  belongsToOnePhase,
  evidenceWeightFor,
  isWeightedEvidence,
  phaseForBlockType,
} from "./blockSchemas";
import {
  SECTION_META,
  getSectionForPhase,
  getSectionMeta,
  getStepSection,
} from "../lessonPlanTheme";

/** Block types each section is not really itself without. Teachers see
 *  sections, not phases, so the old per-phase requirements (Explain needs an
 *  Explanation, Reflect a Reflection, Grow a Mastery map) are held at the
 *  section that contains that phase. */
const REQUIRED_BY_SECTION = {
  assessment_and_ensure: [{ type: "explanation", label: "Explanation" }],
  summary: [
    { type: "reflection", label: "Reflection" },
    { type: "mastery_map", label: "Mastery map" },
  ],
};

/** `{token}` substitution, so a translator gets one whole sentence. */
const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

const identity = (value) => value;

/** Every block in the lesson, with the step that owns it. */
const allBlocks = (steps) =>
  (steps || []).flatMap((step) =>
    (step.blocks || []).map((block) => ({ step, block }))
  );

const sectionCoverageWarnings = (steps, translate) => {
  const warnings = [];

  Object.keys(SECTION_META).forEach((sectionKey) => {
    const label = translate(SECTION_META[sectionKey].label);
    const stepsInSection = (steps || []).filter(
      (step) => getStepSection(step) === sectionKey
    );

    if (stepsInSection.length === 0) {
      warnings.push({
        key: `section-missing-${sectionKey}`,
        where: label,
        text: fill(translate("The lesson has no {phase} step. The rail will skip it."), {
          phase: label,
        }),
      });
      return;
    }
    if (stepsInSection.every((step) => (step.blocks || []).length === 0)) {
      warnings.push({
        key: `section-empty-${sectionKey}`,
        where: label,
        text: fill(translate("{phase} has no content in it yet."), { phase: label }),
      });
      return;
    }

    (REQUIRED_BY_SECTION[sectionKey] || []).forEach((required) => {
      const hasIt = stepsInSection.some((step) =>
        (step.blocks || []).some((block) => block.type === required.type)
      );
      if (!hasIt) {
        warnings.push({
          key: `section-needs-${sectionKey}-${required.type}`,
          where: label,
          text: fill(translate("{phase} has no {block} block."), {
            phase: label,
            block: translate(required.label),
          }),
        });
      }
    });
  });

  return warnings;
};

const masteryWarnings = (concepts, blocks, translate) => {
  const measured = new Set(
    blocks
      .filter(({ block }) => isWeightedEvidence(block.type) && block.concept_key)
      .map(({ block }) => block.concept_key)
  );

  if (!concepts.length) return [];

  if (measured.size === 0) {
    // The trap the design calls out: predictions and reflections are tagged,
    // recorded and visible to the teacher, and carry weight 0 — so the bars
    // stay empty forever and nothing in the editor said why.
    const recordedOnly = blocks.some(
      ({ block }) => block.concept_key && evidenceWeightFor(block.type) === 0
    );
    return [
      {
        key: "no-weighted-evidence",
        where: translate("Mastery"),
        text: recordedOnly
          ? translate(
              "Every tagged block is recorded but never scored, so this lesson can never produce a mastery score. Add an explanation, a graded check or an exploration probe."
            )
          : translate(
              "Nothing carries weighted evidence, so this lesson can never produce a mastery score."
            ),
      },
    ];
  }

  return concepts
    .filter((concept) => concept.concept_key && !measured.has(concept.concept_key))
    .map((concept) => ({
      key: `concept-unmeasured-${concept.concept_key}`,
      where: translate("Mastery"),
      text: fill(translate("\"{concept}\" has no scored evidence, so it will never get a band."), {
        concept: concept.label || concept.concept_key,
      }),
    }));
};

const placementWarnings = (blocks, translate) =>
  blocks
    .map(({ step, block }, index) => {
      if (!belongsToOnePhase(block.type)) return null;
      const home = phaseForBlockType(block.type);
      const homeSection = getSectionForPhase(home);
      const stepSection = getStepSection(step);
      // Sections are what the author sees, so a block is only misplaced when
      // it sits in a different *section* than the one its home phase is in.
      if (!homeSection || !stepSection || homeSection === stepSection) return null;
      return {
        key: `misplaced-${index}`,
        where: `${step.title || step.step_key} · ${block.type}`,
        text: fill(translate("A {type} block belongs in {phase}."), {
          type: block.type,
          phase: translate(getSectionMeta(homeSection).label),
        }),
      };
    })
    .filter(Boolean);

/**
 * `[{ key, where, text }]`, shaped for the Review step's problem list. Empty
 * means every section is covered. `t` is optional; English is the default.
 */
export const loopWarnings = ({ concepts = [], steps = [], t } = {}, tArg) => {
  // `t` is optional (in the options or as a second argument): without it the
  // sentences come back in English.
  const translate = t || tArg || identity;
  const blocks = allBlocks(steps);
  return [
    ...sectionCoverageWarnings(steps, translate),
    ...masteryWarnings(concepts, blocks, translate),
    ...placementWarnings(blocks, translate),
  ];
};
